import { getAvailableResources } from "@q-food/q-food";

const { state, error, execute, isLoading, isReady } = useAsyncState(
  () => getAvailableResources(),
  [],
  {
    immediate: false,
    onError(e) {
      console.error(e);
      throw e;
    }
  }
);

function useApiResources() {
  /**
   * Find a resource by its name e.g.: addresses
   * @param name
   * @returns
   */
  function findByName(name: string) {
    const resource = findByNameSafe(name);
    if (!resource) {
      throw new Error(`Resource with name ${name} not found`);
    }
    return resource;
  }

  function findByNameSafe(name: string) {
    return state.value.find(
      (resource) => resource.name.toLowerCase() === name.toLowerCase()
    );
  }

  /**
   * Find a resource by its uri e.g.: /api/addresses
   * Assumes the uri entered starts with /api/{resource}
   * @param uri
   */
  function findByUri(uri: string) {
    const resource = findByUriSafe(uri);
    if (!resource) {
      throw new Error(`Resource with uri ${uri} not found`);
    }
    return resource;
  }

  function findByUriSafe(uri: string) {
    const newUri = uri.match(/\/api\/[\w-_]+/)?.[0];

    return !newUri
      ? null
      : state.value.find((resource) => resource.uri.includes(newUri));
  }

  /**
   * Find a resource by its title e.g.: Adres
   * @param title
   * @returns
   */
  function findByTitle(title: string) {
    const resource = findByTitleSafe(title);
    if (!resource) {
      throw new Error(`Resource with title ${title} not found`);
    }
    return resource;
  }

  function findByTitleSafe(title: string) {
    return state.value.find(
      (resource) => resource.title.toLowerCase() === title.toLowerCase()
    );
  }

  /**
   * Try to find a resource by its name, uri or title
   * @param name
   * @returns
   */
  function tryFindByAll(str: string) {
    const resource = findByAllSafe(str);
    if (!resource) {
      throw new Error(`Resource with name, uri or title ${str} not found`);
    }
    return resource;
  }

  function findByAllSafe(str: string) {
    return findByNameSafe(str) ?? findByUriSafe(str) ?? findByTitleSafe(str);
  }

  return {
    findByName,
    findByUri,
    findByTitle,
    findByAllSafe,
    findByNameSafe,
    findByUriSafe,
    tryFindByAll,
    state,
    error,
    refreshResources: () => execute(),
    isLoading,
    isReady
  };
}

export { useApiResources };
